import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { 
  HelpCircle,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  X,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown,
  GripVertical
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// FAQ Modal Component
const FAQModal = ({ isOpen, onClose, faq, onSave, maxOrder }) => {
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    order: 0,
    active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (faq) {
      setFormData({
        question: faq.question,
        answer: faq.answer,
        order: faq.order,
        active: faq.active
      });
    } else {
      setFormData({
        question: '',
        answer: '',
        order: maxOrder + 1,
        active: true
      });
    }
    setError('');
  }, [faq, isOpen, maxOrder]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (faq) {
        await axios.put(`${API_URL}/api/admin/faqs/${faq.id}`, formData);
      } else {
        await axios.post(`${API_URL}/api/admin/faqs`, formData);
      }
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl shadow-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white">
            {faq ? 'Edit FAQ' : 'Create FAQ'}
          </h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-180px)]">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Question</label>
            <input
              type="text"
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              placeholder="e.g., What is your minimum investment amount?"
              required
              data-testid="faq-question-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Answer</label>
            <textarea
              value={formData.answer}
              onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
              rows={6}
              className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 resize-none"
              placeholder="Enter the answer to this question..."
              required
              data-testid="faq-answer-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Display Order</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                min="0"
                data-testid="faq-order-input"
              />
              <p className="text-xs text-slate-500 mt-1">Lower numbers appear first</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Status</label>
              <div className="flex items-center gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, active: !formData.active })}
                  className={`relative w-12 h-6 rounded-full transition-colors ${formData.active ? 'bg-emerald-500' : 'bg-slate-600'}`}
                  data-testid="faq-active-toggle"
                >
                  <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.active ? 'translate-x-6' : ''}`} />
                </button>
                <span className={`text-sm ${formData.active ? 'text-emerald-400' : 'text-slate-400'}`}>
                  {formData.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-slate-700 text-white font-semibold rounded-lg hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              data-testid="faq-save-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                faq ? 'Save Changes' : 'Create FAQ'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminFAQs = () => {
  const { user } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const fetchFaqs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/faqs`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  const handleCreate = () => {
    setEditingFaq(null);
    setShowModal(true);
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setShowModal(true);
  };

  const handleDelete = async (faq) => {
    if (!window.confirm(`Are you sure you want to delete this FAQ?\n\n"${faq.question}"`)) return;

    try {
      await axios.delete(`${API_URL}/api/admin/faqs/${faq.id}`);
      setSuccessMessage('FAQ deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchFaqs();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete FAQ');
    }
  };

  const handleToggleActive = async (faq) => {
    setUpdatingId(faq.id);
    try {
      await axios.put(`${API_URL}/api/admin/faqs/${faq.id}`, {
        active: !faq.active
      });
      fetchFaqs();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update FAQ');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleMoveOrder = async (faq, direction) => {
    const currentIndex = faqs.findIndex(f => f.id === faq.id);
    const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (swapIndex < 0 || swapIndex >= faqs.length) return;
    
    const swapFaq = faqs[swapIndex];
    
    setUpdatingId(faq.id);
    try {
      // Swap orders
      await axios.put(`${API_URL}/api/admin/faqs/${faq.id}`, { order: swapFaq.order });
      await axios.put(`${API_URL}/api/admin/faqs/${swapFaq.id}`, { order: faq.order });
      fetchFaqs();
    } catch (error) {
      alert('Failed to reorder FAQs');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSaveSuccess = () => {
    setSuccessMessage(editingFaq ? 'FAQ updated successfully' : 'FAQ created successfully');
    setTimeout(() => setSuccessMessage(''), 3000);
    fetchFaqs();
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 hover:border-slate-600/50 transition-all">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-xs text-slate-400">{label}</p>
        </div>
      </div>
    </div>
  );

  const activeCount = faqs.filter(f => f.active).length;
  const inactiveCount = faqs.filter(f => !f.active).length;
  const maxOrder = faqs.length > 0 ? Math.max(...faqs.map(f => f.order)) : 0;

  return (
    <AdminLayout>
      {/* Modal */}
      <FAQModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        faq={editingFaq}
        onSave={handleSaveSuccess}
        maxOrder={maxOrder}
      />

      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-emerald-400">
          <CheckCircle className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2" data-testid="faqs-title">
            FAQs
          </h1>
          <p className="text-slate-400">
            Manage frequently asked questions
          </p>
        </div>
        <button
          onClick={handleCreate}
          data-testid="create-faq-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create FAQ
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard 
          icon={HelpCircle} 
          label="Total FAQs" 
          value={faqs.length} 
          color="bg-slate-500/20 text-slate-400" 
        />
        <StatCard 
          icon={Eye} 
          label="Active" 
          value={activeCount} 
          color="bg-emerald-500/20 text-emerald-400" 
        />
        <StatCard 
          icon={EyeOff} 
          label="Inactive" 
          value={inactiveCount} 
          color="bg-red-500/20 text-red-400" 
        />
      </div>

      {/* FAQs Table */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : faqs.length === 0 ? (
          <div className="text-center py-12">
            <HelpCircle className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No FAQs found</p>
            <p className="text-slate-500 text-sm mt-1">Create your first FAQ to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4 w-12">
                    <GripVertical className="w-4 h-4" />
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    Question
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4 w-24">
                    Order
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4 w-32">
                    Status
                  </th>
                  <th className="text-right text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4 w-48">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {faqs.map((faq, index) => (
                  <tr key={faq.id} className="hover:bg-slate-700/20 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleMoveOrder(faq, 'up')}
                          disabled={index === 0 || updatingId === faq.id}
                          className="p-1 text-slate-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleMoveOrder(faq, 'down')}
                          disabled={index === faqs.length - 1 || updatingId === faq.id}
                          className="p-1 text-slate-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <span className="font-medium text-white">{faq.question}</span>
                        <p className="text-slate-500 text-sm mt-1 truncate max-w-lg">
                          {faq.answer.length > 100 ? `${faq.answer.substring(0, 100)}...` : faq.answer}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-400 font-mono">{faq.order}</span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleActive(faq)}
                        disabled={updatingId === faq.id}
                        data-testid={`toggle-active-${faq.id}`}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          faq.active 
                            ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' 
                            : 'bg-slate-600/50 text-slate-400 hover:bg-slate-600'
                        }`}
                      >
                        {updatingId === faq.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : faq.active ? (
                          <Eye className="w-3 h-3" />
                        ) : (
                          <EyeOff className="w-3 h-3" />
                        )}
                        {faq.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(faq)}
                          data-testid={`edit-faq-${faq.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(faq)}
                          data-testid={`delete-faq-${faq.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminFAQs;
